import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import {
    type AegOccurrence,
    type AegOccurrenceId,
    buildAegOccurrenceUpdate,
    fetchAegOccurrence,
    updateAegOccurrence,
} from '../api/aegOccurrences';
import { formatCurrency, formatDate } from '../components/aeg/config';
import {
    AEG_STAGE_BY_STATUS,
    AegAssignToEnum,
    type AegStatusEnum,
    AegRepairScopeEnum,
    formatAegAssignTo,
    formatAegEnum,
    getAegAssignToOptions,
    type AegEnumGroup,
    type StageKey,
} from '../components/aeg/types';

const ASSIGN_TO_VALUES = new Set<string>(Object.values(AegAssignToEnum));

export type AegDetailField = {
    label: string;
    value: string;
};

export type AegRequestDetails = {
    stage?: StageKey;
    client: string;
    model: string;
    plate: string;
    chassis: string;
    assignTo: string;
    assignToLabel: string;
    assignOptions: { value: string; label: string }[];
    repairValue: string;
    daysSinceOpening: string;
    approvalType: string;
    requestType: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    serviceType: string;
    warrantyType: string;
    stageTime: string;
    osNumber: string;
    occurrenceNumber: string;
    aegNumber: string | null;
    dealer: string;
    regional: string;
    responsible: string;
    phone: string;
    openingDate: string;
    sgNumber: string;
    labor: string;
    attendance: string;
    hasThirdParty: string;
    invoiceValue: string;
    thirdPartyServices: string;
    invoiceNumbers: string;
    repairScope: string;
    repairDate: string;
    km: string;
    controlCode: string;
    chameVolks: string;
    breakdown: string;
    fleetComposition: string;
    lastPurchase: string;
    companies: string;
    customerComplaint: string;
    dnDiagnosis: string;
    dnJustification: string;
    vehicle: AegDetailField[];
};

const text = (value?: string | number | null) => {
    if (value === null || value === undefined || String(value).trim() === '') {
        return '—';
    }
    return String(value);
};

const currency = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const amount = Number(value);
    return Number.isNaN(amount) ? '—' : formatCurrency(amount);
};

const kmLabel = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const km = Number(value);
    return Number.isNaN(km) ? '—' : `${km.toLocaleString('pt-BR')} km`;
};

const laborLabel = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    return typeof value === 'number' ? value.toLocaleString('pt-BR') : value;
};

const yesNo = (intl: IntlShape, value?: boolean | null) => {
    if (value === true) {
        return intl.formatMessage({ id: 'intl-msg:support.aeg.detail.history.yes' });
    }
    if (value === false) {
        return intl.formatMessage({ id: 'intl-msg:support.aeg.detail.history.no' });
    }
    return '—';
};

const flag = (intl: IntlShape, value?: boolean | string | null) =>
    typeof value === 'boolean' ? yesNo(intl, value) : text(value);

const personName = (value?: string | { name?: string } | null) => {
    if (!value) {
        return '—';
    }
    return typeof value === 'string' ? text(value) : text(value.name);
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '—';
    }
    if (!value.includes('T')) {
        return formatDate(value);
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const daysSince = (intl: IntlShape, value?: string | null) => {
    if (!value) {
        return '—';
    }
    const created = new Date(value.includes('T') ? value : `${value}T12:00:00`).getTime();
    if (Number.isNaN(created)) {
        return '—';
    }
    const days = Math.max(0, Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24)));
    return intl.formatMessage({ id: 'intl-msg:support.aeg.detail.summary.days' }, { count: days });
};

const enumText = (intl: IntlShape, group: AegEnumGroup, value?: string | null, other?: string | null) => {
    if (!value) {
        return '—';
    }
    const label = formatAegEnum(intl, group, value);
    if (!label) {
        return '—';
    }
    if (value === 'OTHERS' && other) {
        return `${label}: ${other}`;
    }
    return label;
};

const assignToValue = (occurrence: AegOccurrence) => {
    const candidates = [occurrence.assignTo, typeof occurrence.assignedTo === 'string' ? occurrence.assignedTo : ''];
    return candidates.find(value => value && ASSIGN_TO_VALUES.has(value)) ?? '';
};

const assignLabel = (intl: IntlShape, occurrence: AegOccurrence, value: string) => {
    if (value) {
        return formatAegAssignTo(intl, value);
    }
    if (typeof occurrence.assignedTo === 'string' && occurrence.assignedTo) {
        return occurrence.assignedTo;
    }
    return personName(occurrence.assignedTo);
};

const repairScopeLabel = (intl: IntlShape, occurrence: AegOccurrence) => {
    if (!occurrence.repairScope) {
        return '—';
    }
    const label = formatAegEnum(intl, 'repairScope', occurrence.repairScope);
    if (occurrence.repairScope === AegRepairScopeEnum.PARTIAL && occurrence.partialPercentage) {
        return `${label} (${occurrence.partialPercentage}%)`;
    }
    return label || '—';
};

const buildAegDetails = (occurrence: AegOccurrence, intl: IntlShape): AegRequestDetails => {
    const client = occurrence.customer ?? occurrence.client ?? '';
    const model = occurrence.model ?? '';
    const plate = occurrence.licensePlate ?? occurrence.plate ?? '';
    const chassis = occurrence.chassis ?? '';
    const vehicle = occurrence.vehicle;
    const assignTo = assignToValue(occurrence);
    const assignOptions = getAegAssignToOptions(intl);
    const status = occurrence.status ?? occurrence.moveTo;
    const implement = occurrence.implementOrBodyBuilder ?? occurrence.implement;
    const implementOther = occurrence.implementOrBodyBuilderOther ?? occurrence.implementOther;
    const volksConfia =
        vehicle?.hasVolksConfiaWarranty !== undefined
            ? vehicle.hasVolksConfiaWarranty
            : occurrence.warrantyType
              ? occurrence.warrantyType === 'VolksConfia'
              : undefined;
    const createdAt = occurrence.createdAt ?? occurrence.date;

    return {
        stage: status ? AEG_STAGE_BY_STATUS[status] : undefined,
        client: text(client),
        model: text(model),
        plate: text(plate),
        chassis: text(chassis),
        assignTo,
        assignToLabel: assignLabel(intl, occurrence, assignTo),
        assignOptions,
        repairValue: currency(occurrence.repairTotal),
        daysSinceOpening: daysSince(intl, createdAt),
        approvalType: enumText(intl, 'approvalType', occurrence.approvalType),
        requestType: enumText(intl, 'requestType', occurrence.requestType),
        createdBy: personName(occurrence.createdBy),
        createdAt: formatDate(createdAt),
        updatedAt: formatDateTime(occurrence.updatedAt),
        serviceType: enumText(intl, 'serviceType', occurrence.serviceType),
        warrantyType: text(occurrence.warrantyType),
        stageTime: text(occurrence.stageTime),
        osNumber: text(occurrence.osNumber ?? occurrence.serviceOrder),
        occurrenceNumber: text(occurrence.occurrence),
        aegNumber: occurrence.aegNumber ? text(occurrence.aegNumber) : null,
        dealer: text(occurrence.dn ?? occurrence.dealer),
        regional: text(occurrence.regional),
        responsible: text(occurrence.responsible),
        phone: text(occurrence.phone),
        openingDate: formatDate(createdAt),
        sgNumber: text(occurrence.sgNumber ?? occurrence.warrantyRequest),
        labor: laborLabel(occurrence.laborTotalUt ?? occurrence.laborTotal),
        attendance: enumText(
            intl,
            'attendanceCondition',
            occurrence.attendanceCondition ?? occurrence.serviceCondition
        ),
        hasThirdParty: yesNo(intl, occurrence.hasThirdPartyService ?? occurrence.hasThirdParty),
        invoiceValue: currency(occurrence.thirdPartyInvoiceAmount ?? occurrence.thirdPartyInvoiceValue),
        thirdPartyServices: text(occurrence.thirdPartyServices),
        invoiceNumbers: text(occurrence.thirdPartyInvoiceNumbers ?? occurrence.thirdPartyInvoiceNumber),
        repairScope: repairScopeLabel(intl, occurrence),
        repairDate: formatDate(occurrence.repairDate),
        km: kmLabel(occurrence.odometer),
        controlCode: text(occurrence.controlCode),
        chameVolks: text(occurrence.chameVolksNumber ?? occurrence.chameVolks),
        breakdown: yesNo(intl, occurrence.breakdown),
        fleetComposition: text(occurrence.fleetComposition),
        lastPurchase: formatDateTime(occurrence.lastVwcoManPurchase ?? occurrence.lastPurchase),
        companies: text(occurrence.companies),
        customerComplaint: text(occurrence.customerComplaint),
        dnDiagnosis: text(occurrence.dnDiagnosis),
        dnJustification: text(occurrence.dnJustification),
        vehicle: [
            { label: 'support.aeg.newRequest.vehicleData.owner', value: text(vehicle?.vehicleOwner ?? client) },
            { label: 'support.aeg.newRequest.vehicleData.model', value: text(model) },
            { label: 'support.aeg.newRequest.vehicleData.chassis', value: text(chassis) },
            { label: 'support.aeg.newRequest.vehicleData.licensePlate', value: text(plate) },
            {
                label: 'support.aeg.newRequest.vehicleData.vehicleType',
                value: enumText(intl, 'vehicleType', occurrence.vehicleType),
            },
            { label: 'support.aeg.newRequest.vehicleData.color', value: text(vehicle?.color) },
            {
                label: 'support.aeg.newRequest.vehicleData.implement',
                value: enumText(intl, 'implementOrBodyBuilder', implement, implementOther),
            },
            { label: 'support.aeg.newRequest.vehicleData.productionDate', value: formatDate(vehicle?.productionDate) },
            { label: 'support.aeg.newRequest.vehicleData.billingDate', value: formatDate(vehicle?.billingDate) },
            {
                label: 'support.aeg.newRequest.vehicleData.saleDate',
                value: formatDate(vehicle?.saleDate ?? occurrence.saleDate),
            },
            { label: 'support.aeg.newRequest.vehicleData.tsDeliveryDate', value: formatDate(vehicle?.tsDeliveryDate) },
            { label: 'support.aeg.newRequest.vehicleData.responsibleDealer', value: text(vehicle?.responsibleDealer) },
            { label: 'support.aeg.newRequest.vehicleData.warrantyZeroEnd', value: formatDate(vehicle?.warrantyEnd) },
            {
                label: 'support.aeg.newRequest.vehicleData.additionalWarrantyEnd',
                value: formatDate(vehicle?.additionalWarrantyEnd),
            },
            { label: 'support.aeg.newRequest.vehicleData.maintenanceContract', value: text(vehicle?.contractNumber) },
            {
                label: 'support.aeg.newRequest.vehicleData.specialWarranty',
                value: formatDate(vehicle?.specialWarrantyEnd),
            },
            { label: 'support.aeg.newRequest.vehicleData.pendingCampaigns', value: text(vehicle?.campaigns) },
            { label: 'support.aeg.newRequest.vehicleData.checkLegal', value: flag(intl, vehicle?.consultLegal) },
            { label: 'support.aeg.newRequest.vehicleData.blockedVehicle', value: yesNo(intl, vehicle?.vehicleBlocked) },
            { label: 'support.aeg.newRequest.vehicleData.warranty', value: yesNo(intl, vehicle?.hasWarranty) },
            { label: 'support.aeg.newRequest.vehicleData.volksConfiaWarranty', value: yesNo(intl, volksConfia) },
            {
                label: 'support.aeg.newRequest.vehicleData.lastUpdate',
                value: formatDateTime(vehicle?.updatedAt ?? occurrence.updatedAt),
            },
        ],
    };
};

export const useAegDetails = (aegOccurrenceId?: AegOccurrenceId | null) => {
    const intl = useIntl();
    const [occurrence, setOccurrence] = useState<AegOccurrence | null>(null);
    const [loading, setLoading] = useState(Boolean(aegOccurrenceId));
    const [error, setError] = useState(false);

    const loadOccurrence = useCallback(async () => {
        if (!aegOccurrenceId) {
            setOccurrence(null);
            setLoading(false);
            setError(false);
            return;
        }

        setOccurrence(null);
        setLoading(true);
        setError(false);

        try {
            setOccurrence(await fetchAegOccurrence(aegOccurrenceId));
        } catch {
            setOccurrence(null);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [aegOccurrenceId]);

    useEffect(() => {
        void loadOccurrence();
    }, [loadOccurrence]);

    const save = useCallback(
        async (changes: { status?: AegStatusEnum; assignTo?: string }) => {
            if (!aegOccurrenceId || !occurrence) {
                throw new Error('Erro ao atualizar solicitação AEG');
            }

            const updated = await updateAegOccurrence(aegOccurrenceId, buildAegOccurrenceUpdate(occurrence, changes));
            setOccurrence(current =>
                current
                    ? {
                          ...current,
                          status: updated.status ?? changes.status ?? current.status,
                          moveTo: updated.moveTo ?? changes.status ?? current.moveTo,
                          assignTo: updated.assignTo ?? changes.assignTo ?? current.assignTo,
                      }
                    : current
            );
        },
        [aegOccurrenceId, occurrence]
    );

    const details = useMemo(() => (occurrence ? buildAegDetails(occurrence, intl) : null), [occurrence, intl]);

    return {
        details,
        loading,
        error,
        reload: loadOccurrence,
        save,
    };
};

export type AegDetailsController = ReturnType<typeof useAegDetails>;
